import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { IFinanceWashout } from "@/Washouts/types/Api/Finance";
import { ApproveAllWashoutParams, UpdateWashoutParams, UpdateWashoutStatusParams } from "@/Washouts/types/Api/commons";

// Common fragments
import { CompensationFragment, ReviewFragment } from "../../common/fragments";

export async function updateFinanceWashout(...params: UpdateWashoutParams) {
	const [locationId, dataInput, position] = params;
	const query = gql`
		mutation updateFinanceWashout($locationId: String!, $input: WashoutInput!, $position: String) {
			updateFinanceWashout(locationId: $locationId, input: $input, position: $position) {
				_id
				commissionCycleId
				payPeriodId
				userId
				summary {
					...Compensation
				}
			}
		}
		${CompensationFragment}
	`;

	const variables = {
		locationId,
		position,
		input: dataInput,
	};

	try {
		const response: GraphqlQueryResponse<
			"updateFinanceWashout",
			Pick<IFinanceWashout, "_id" | "commissionCycleId" | "payPeriodId" | "userId" | "summary">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateFinanceWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function updateFinanceWashoutStatus(...params: UpdateWashoutStatusParams) {
	const [locationId, commissionCycleId, payPeriodId, userId, input, position] = params;
	const query = gql`
		mutation updateFinanceWashoutStatus(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$userId: String!
			$reviewInput: WashoutReviewInput
			$position: String
		) {
			updateFinanceWashoutStatus(
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				locationId: $locationId
				reviewInput: $reviewInput
				userId: $userId
				position: $position
			) {
				_id
				commissionCycleId
				payPeriodId
				userId
				position
				status
				review {
					...Review
				}
				employee {
					position
				}
			}
		}
		${ReviewFragment}
	`;

	const variables = { locationId, commissionCycleId, payPeriodId, userId, position, reviewInput: input };

	try {
		const response: GraphqlQueryResponse<
			"updateFinanceWashoutStatus",
			Pick<IFinanceWashout, "_id" | "commissionCycleId" | "payPeriodId" | "userId" | "review" | "employee">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateFinanceWashoutStatus;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function approveAllFinanceWashout(...params: ApproveAllWashoutParams) {
	const [userId, locationId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		mutation approveAllFinanceWashout(
			$userId: String!
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String
		) {
			approveAllFinanceWashout(
				userId: $userId
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				success
			}
		}
	`;
	const variables = {
		userId,
		locationId,
		commissionCycleId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"approveAllFinanceWashout", { success: boolean }> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.approveAllFinanceWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function generateFinanceWashout(locationId: string, commissionCycleId: string, payPeriodId: string, userIds: string[]) {
	const query = gql`
		mutation generateWashouts($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $userIds: [String!]!) {
			generateWashouts(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId, userIds: $userIds) {
				success
			}
		}
	`;
	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		userIds,
	};
	try {
		const response: GraphqlQueryResponse<"generateWashouts", { success: boolean }> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.generateWashouts;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
